import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { DerivedAuthService } from '../services';
import { ObjectType } from '../types';

/**
 * HTTP interceptor that attaches authentication headers to outgoing requests.
 *
 * Header injection strategy:
 * - Bypasses GraphQL (`/api/collections/read`), Wagtail, Stackdriver, and BigQuery endpoints.
 * - For the student-balance endpoint, injects `Authorization` and `AuthenticationType`
 *   using the current user's auth token.
 * - For all other authenticated requests, injects `Authentication` (Bearer token)
 *   and `AuthenticationType` unless the header is already present on the request.
 */
export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(DerivedAuthService);
  const isAuthentication = authService.isAuthenticated;
  const customHeader: ObjectType = {};

  if (
    request.url.includes('/api/collections/read') ||
    request.url.includes('/api/collections/write')
  ) {
    // Bypass auth headers for Firestore collection read/write endpoints
    return next(request);
  }
  if (request.url.includes('/en/api/v2/pages/')) {
    return next(request);
  }
  if (request.url.includes('/microApp/api/stackdriver/logging')) {
    return next(request);
  }
  if (request.url.includes('/microApp/api/bigquery/insert')) {
    return next(request);
  }
  if (request.url.includes('/taskstream/upsertaccount')) {
    return next(request);
  }
  if (request.url.includes('/microApp/api/common/studentbalance')) {
    customHeader['Authorization'] = `Bearer ${authService.authToken()}`;
    customHeader['AuthenticationType'] = authService.authType() || ' ';
    const clonedRequest = request.clone({
      setHeaders: customHeader,
    });
    return next(clonedRequest);
  }

  if (isAuthentication()) {
    customHeader['Authentication'] = `Bearer ${authService.authToken()}`;
    if (
      !request.headers.has('AuthenticationType') &&
      !request.url.includes('/api/collections/write')
    ) {
      customHeader['AuthenticationType'] = authService.authType() || ' ';
    }
  }
  const clonedRequest = request.clone({
    setHeaders: customHeader,
  });

  return next(clonedRequest);
};
